import { useState } from "react";
import { ArrowUpRight, Clock, MapPin, Paperclip } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { JOBS } from "@/data/content";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PERKS = ["Follow-the-sun teams", "US client exposure", "Learning budget", "Performance bonuses", "Hybrid in Hyderabad", "No deck-only work"];

export default function Careers() {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [resumeFile, setResumeFile] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    if (!resumeFile) {
      setError("Please attach your CV or portfolio (PDF, DOC or DOCX).");
      return;
    }
    setError("");
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("role", selected.title);
      fd.append("message", form.message || `Application for ${selected.title}`);
      fd.append("resume", resumeFile);
      await api.post("/enquiries/career", fd);
      toast.success("Application sent — our talent team will be in touch.");
      setSelected(null);
      setForm({ name: "", email: "", message: "" });
      setResumeFile(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white placeholder:text-dim outline-none transition-colors focus:border-brand";

  return (
    <div data-testid="careers-page">
      <Seo
        title="Careers — Vigarbiz"
        siteName="Vigarbiz"
        description="Join Vigarbiz in Hyderabad or San Francisco: performance marketing, SEO, content, engineering and client services roles."
      />
      <PageHeader
        overline="Careers"
        lines={["Do the best", "Work of your", <span key="l" className="text-brand">Life here.</span>]}
        description="Small pods, real budgets, direct client access. If you want to hide behind process, look elsewhere. If you want your work live by Friday, welcome."
      />

      <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap gap-2" data-testid="careers-perks">
            {PERKS.map((p) => (
              <span key={p} className="rounded-full border border-white/10 bg-surface px-4 py-2 text-xs text-muted2">
                {p}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">Open roles — {JOBS.length}</p>
        </Reveal>
        <div className="mt-6 divide-y divide-white/10 border-y border-white/10">
          {JOBS.map((job, i) => (
            <Reveal key={job.id} delay={i * 0.04} y={20}>
              <div className="group flex flex-col gap-5 py-8 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-dim">{job.dept}</p>
                  <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-brand sm:text-3xl">
                    {job.title}
                  </h3>
                  <p className="mt-2 max-w-xl text-sm text-muted2">{job.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-[0.15em] text-dim">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-brand" /> {job.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-brand" /> {job.type}
                    </span>
                  </div>
                </div>
                <button
                  data-testid={`job-apply-button-${job.id}`}
                  onClick={() => setSelected(job)}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-display text-sm font-bold text-white transition-colors duration-300 hover:border-brand hover:bg-brand hover:text-ink active:scale-95"
                >
                  Apply Now
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="border-white/10 bg-surface text-white sm:max-w-lg" data-testid="career-apply-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold">
              Apply — {selected?.title}
            </DialogTitle>
            <DialogDescription className="text-muted2">
              {selected?.location} · {selected?.type}. Two minutes, no cover letter theater.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="mt-2 space-y-4">
            <div>
              <label htmlFor="apply-name" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                Full name *
              </label>
              <input
                id="apply-name"
                data-testid="career-name-input"
                className={inputCls}
                placeholder="Priya Sharma"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="apply-email" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                Email *
              </label>
              <input
                id="apply-email"
                type="email"
                data-testid="career-email-input"
                className={inputCls}
                placeholder="priya@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="apply-message" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                Why you + portfolio link
              </label>
              <textarea
                id="apply-message"
                rows={4}
                data-testid="career-message-input"
                className={inputCls}
                placeholder="Three sentences on the result you're proudest of, plus a link."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="apply-resume" className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted2">
                CV / portfolio (PDF, DOC, DOCX — max 10 MB) *
              </label>
              <label
                htmlFor="apply-resume"
                data-testid="career-resume-dropzone"
                className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-white/20 bg-ink px-4 py-4 text-sm text-muted2 transition-colors hover:border-brand/60 hover:text-white"
              >
                <Paperclip className="h-4 w-4 shrink-0 text-brand" />
                {resumeFile ? resumeFile.name : "Attach your file"}
              </label>
              <input
                id="apply-resume"
                type="file"
                accept=".pdf,.doc,.docx"
                data-testid="career-resume-input"
                className="hidden"
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
              />
            </div>
            {error && (
              <p data-testid="career-form-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={sending}
              data-testid="career-form-submit-button"
              className="w-full rounded-full bg-brand py-3.5 font-display text-sm font-bold text-ink transition-colors duration-300 hover:bg-brand-hover active:scale-95 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Submit Application"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
